import axios from 'axios'
import { orderStatuses } from '@/util/globals'
import { DateTime } from 'luxon'
import router from '@/router/index'
import PaymentStatus from '@/models/enums/PaymentStatus'
import OrderStatus from '@/models/enums/OrderStatus'
import { orderCancelPointError } from '@/util/order-cancel'

// const qs = require('qs')

// Ответ в форме ошибки axios: вызывающие показывают res.response.data.message
const errorResponse = message => ({ status: 0, response: { data: { message } } })

const updateErrorMessage = errorResponse('Редактирование заказа не разрешено')

const state = {
  ordersFilterDate: DateTime.local().toFormat('y-MM-dd'),
  ordersSearchString: '',
  currentOrderInfo: {},
  currentEditOrder: null,
  orders: [],
  orderStatuses,
  ordersFetchSource: null,
  showPaymentDialog: false,
}

const getters = {
  getOrdersCount: state => {
    return state.orders.length
  },

  getSelectedDate: state => {
    return state.ordersFilterDate || DateTime.local().toFormat('y-MM-dd')
  },

  isNewOrderPage: (state) => {
    return router.currentRoute.name === 'newOrder'
  },
  isOrderPaid: state => {
    return state.currentEditOrder?.payment?.status === PaymentStatus.Success
  },
  // Время исполнения менять нельзя: заказ закрыт или его таймер уже остановлен
  // (зеркало бэкендного EditGuard::assertTimeEditable)
  isTimeChangeLocked (state) {
    const order = state.currentEditOrder
    if (!order || !order.orderStatus) {
      return false
    }
    return OrderStatus.closedStatuses.includes(order.orderStatus) || Boolean(order.timerStoppedAt)
  },
  // Почему заблокирована смена времени - текст подсказки для кнопки часов
  timeChangeLockedMessage (state, getters) {
    if (!getters.isTimeChangeLocked) {
      return null
    }
    if (OrderStatus.closedStatuses.includes(state.currentEditOrder.orderStatus)) {
      return 'Время изменить уже нельзя: заказ завершён или отменён'
    }
    return 'Время изменить уже нельзя: заказ готов, таймер остановлен'
  },
}

const actions = {
  printingCheck ({ rootGetters }, data) {
    return axios.post(rootGetters['api/printingCheck'], data).then(res => {
      return res
    })
  },
  emitCallback ({ rootGetters, commit }, data) {
    return axios
      .post(rootGetters['api/ordersEmitCallback'], data)
      .then(res => {
        const orderData = res.data.data
        commit('replaceOrderInList', {
          id: orderData.id,
          data: orderData,
        })
        return orderData
      })
  },
  fetchStatusesList ({ rootGetters, dispatch }, meta) {
    /* Make unique request, other pending will be canceled */
    const url = rootGetters['api/ordersStatusesList']
    const source = axios.CancelToken.source()
    dispatch('requests/addUniqueRequest', { url, source }, { root: true })

    return axios
      .get(url, {
        params: meta,
        cancelToken: source.token,
      })
      .then(res => {
        return res.data.data
      })
  },

  fetchOrdersList ({ commit, rootGetters, state, dispatch }, meta) {
    /* Make unique request, other pending will be canceled */
    const url = rootGetters['api/ordersList']
    const source = axios.CancelToken.source()
    dispatch('requests/addUniqueRequest', { url, source }, { root: true })

    return axios
      .get(url, {
        params: meta,
        cancelToken: source.token,
      })
      .then(res => {
        commit('setOrdersList', res.data.items)
        return res
      })
      // .catch(err => err)
  },
  fetchSingleOrders ({ commit, rootGetters }, id) {
    return axios
      .get(rootGetters['api/ordersSingle'](id)).then(res => {
        return res
      })
      // .catch(err => err)
  },
  createOrders ({ rootGetters }, data) {
    return axios
      .post(rootGetters['api/ordersCreate'], data).then(res => {
        return res
      }).catch(err => err)
  },
  async cancelOrder ({ rootGetters, commit }, id) {
    const error = await orderCancelPointError(rootGetters)
    if (error) {
      return errorResponse(error)
    }

    return axios.post(rootGetters['api/ordersCancelOrder'](id)).then(res => {
      const orderData = res.data.data
      commit('replaceOrderInList', {
        id: orderData.id,
        data: orderData,
      })
      return res
    }).catch(err => err)
  },
  confirmOrder ({ rootGetters, commit }, id) {
    return axios.post(rootGetters['api/ordersConfirmOrder'](id)).then(res => {
      return res
    }).catch(err => err)
  },
  changeOnNextStatus ({ rootGetters, commit }, id) {
    return axios.patch(rootGetters['api/ordersChangeOnNextStatus'](id)).then(res => {
      const orderData = res.data.data
      commit('replaceOrderInList', {
        id: orderData.id,
        data: orderData,
      })
      return res
    })
  },
  updateOrder ({ rootGetters, commit, state }, { id, data }) {
    const orderStatus = state.currentEditOrder ? state.currentEditOrder.orderStatus : null
    if (orderStatus && ['indelivery', 'completed', 'canceled'].includes(orderStatus.toLowerCase())) {
      return updateErrorMessage
    }
    return axios.patch(rootGetters['api/updateOrder'](id), data).then(res => {
      return res
    }).catch(err => err)
  },
  changeOrderCourier ({ rootGetters, commit, state }, { id, courierId }) {
    const orderStatus = state.currentEditOrder ? state.currentEditOrder.orderStatus : null
    if (orderStatus && ['completed', 'canceled'].includes(orderStatus.toLowerCase())) {
      return updateErrorMessage
    }
    const data = {
      courierId: courierId,
    }
    return axios.patch(rootGetters['api/updateOrder'](id), data).then(res => {
      return res.data.data
    })
  },

  startPreparing ({ rootGetters, commit }, id) {
    return axios.post(rootGetters['api/ordersStartPreparing'](id)).then(res => {
        const orderData = res.data.data
        commit('replaceOrderInList', {
          id: orderData.id,
          data: orderData,
        })
      return res
    })
  },
  fetchOrderStateLog ({ rootGetters, dispatch }, id) {
    /* Make unique request, other pending will be canceled */
    const url = rootGetters['api/getOrderStateLog'](id)
    const source = axios.CancelToken.source()
    dispatch('requests/addUniqueRequest', { url, source }, { root: true })

    return axios
      .get(url, {
        cancelToken: source.token,
      })
      .then(res => {
        return res.data.items
      })
  },
  refreshCurrentOrder ({ dispatch, commit }, id) {
    dispatch('resetCurrentOrder')

    dispatch('fetchSingleOrders', id)
      .then(res => {
        const data = res.data.data
        commit('baskets/setOrderStatus', data.orderStatus, { root: true })
        commit('setCurrentEditOrder', data)
        commit('fastOrder/setOrderId', id, { root: true })
        commit('baskets/setBasket', data.basket, { root: true })
        commit('baskets/setOriginalBasket', data.basket, { root: true })
        commit('fastOrder/setClientAddress', data.clientAddress, { root: true })
      })
  },
  // После операции терминала: только статус заказа и платёж. В отличие от refreshCurrentOrder
  // страницу не сбрасывает — шаг, открытые окна и состав корзины остаются как есть
  refreshCurrentOrderPayment ({ state, rootState, dispatch, commit }, id) {
    return dispatch('fetchSingleOrders', id).then(res => {
      const data = res.data.data
      const current = state.currentEditOrder
      // пока шёл запрос, оператор мог уйти на другой заказ
      if (String(current?.id) !== String(id)) return

      // оплата меняет payment_type и у заказа, и у корзины
      const paymentType = data.basket?.paymentType
      commit('setCurrentEditOrder', {
        ...current,
        orderStatus: data.orderStatus,
        orderStatusDescription: data.orderStatusDescription,
        canceledAt: data.canceledAt,
        payment: data.payment,
        paymentType: data.paymentType,
        paymentMode: data.paymentMode,
        initialPaymentType: data.initialPaymentType,
        basket: current.basket ? { ...current.basket, paymentType } : current.basket,
      })
      commit('baskets/setOrderStatus', data.orderStatus, { root: true })

      const basket = rootState.baskets.basket
      if (basket && String(basket.id) === String(data.basket?.id)) {
        commit('baskets/setBasket', { ...basket, paymentType }, { root: true })
      }
    })
  },
  resetCurrentOrder ({ commit }) {
    commit('fastOrder/setStep', 0, { root: true })
    commit('baskets/setOrderStatus', null, { root: true })
    commit('fastOrder/setDrinkDetailsStatus', false, { root: true })
    commit('fastOrder/setSauceDetailsStatus', false, { root: true })
    commit('fastOrder/togglePromotionsWindow', false, { root: true })
    commit('fastOrder/toggleCommentKeyboard', false, { root: true })
    commit('fastOrder/setCurrentOtherWindow', undefined, { root: true })
    commit('fastOrder/setSecondStepWindow', 'address', { root: true })
    commit('fastOrder/setCurrentPaymentTab', 'paymentTypes', { root: true })
    commit('baskets/setBasket', {}, { root: true })
    commit('baskets/setOriginalBasket', {}, { root: true })
    commit('setCurrentEditOrder', {})
    commit('clients/setClientsAddress', [], { root: true })
    commit('app/setRequestFilialId', null, { root: true })
    commit('app/setRequestRegionId', null, { root: true })
  },
}

const mutations = {
  setOrdersSearchString (state, value) {
    state.ordersSearchString = value
  },
  setFilterDate (state, payload) {
    state.ordersFilterDate = payload
  },
  setOrdersList (state, payload) {
    state.orders = payload
  },
  clearOrdersList (state, payload) {
    state.orders = []
  },
  replaceOrderInList (state, { id, data }) {
    const orders = state.orders
    state.orders = []
    console.log({ id: id, data: data })
    for (let i = 0; i < orders.length; i++) {
      if (orders[i].id === id) {
        orders[i] = data
        break
      }
    }
    state.orders = orders
  },
  setCurrentOrderInfo (state, payload) {
    state.currentOrderInfo = payload
  },
  setCurrentEditOrder (state, value) {
    state.currentEditOrder = value
  },
  setOrdersFetchSource (state, source) {
    state.ordersFetchSource = source
  },
  setOrderLateness (state, { id, value }) {
    state.orders.map(order => {
      if (order.id === id) {
        // console.log('--- setOrderLateness --> ', value)
        order.isLateness = !!value
      }
      return order
    })
  },
  setShowPaymentDialog (state, value) {
    state.showPaymentDialog = value
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
